import React, { useState, useRef, useCallback, useContext } from "react";
import { UserContext } from "context";
import { Box, Menu, Button, MenuItem } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { hasUserGroup } from "utils/shared";
import { useSelector } from "react-redux";
import OffCycleVarianceDialog from "./off-cycle-varianceDialog";
import GlobalBreakFixDialog from "./globalBreakfixDialog";

const typeConstant = Object.freeze({
  OFF_CYCLE_VARIANCE: "OFF_CYCLE_VARIANCE",
  BREAKFIX: "BREAKFIX",
  GLOBAL_BREAKFIX: "GLOBAL_BREAKFIX",
});
const actionTypes = Object.freeze({
  OFF_CYCLE_VARIANCE: "Off-Cycle Variance",
  BREAKFIX: "BreakFix",
});
const title = Object.freeze({
  OFF_CYCLE_VARIANCE: "Create Variance",
  BREAKFIX: "Create Break Fix",
});
const columnsName = Object.freeze({
  OFF_CYCLE_VARIANCE: {
    LINE_OF_BUSINESS: "Line of Business",
    EFFECTIVE_DATE: "Effective Date",
    COMMENTS: "Variance Comments",
    OPTIONS: "Adopt Variance",
  },
  BREAKFIX: {
    LINE_OF_BUSINESS: "Line of Business",
    EFFECTIVE_DATE: "Effective Date",
    COMMENTS: "Break Fix Comments",
    OPTIONS: "Apply Break Fix",
  },
});

export default function RowAction({
  CAB,
  breakFix,
  hasRestrictedChildPolicy = false,
  codingComplete,
  translationComplete,
  configurationComplete,
  policyKey,
  policyNumber,
  policyTitle,
  reloadGrid,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedType, setselectedType] = useState(null);

  const closeDialog = () => setselectedType(null);
  const userContext = useContext(UserContext);
  const open = Boolean(anchorEl);
  const selectedHealthPlan = useSelector(
    (state) => state.healthPlan.healthPlan
  );
  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const group1 = [
    "Poet-Avalon-POE",
    "Poet-Avalon-Admin",
    "Poet-Avalon-Quality",
  ];
  const group2 = ["Poet-Avalon-POE", "Poet-Avalon-Admin"];
  const healthPlanKey = selectedHealthPlan?.healthPlanKey;

  const canShowBreakFix =
    hasUserGroup(userContext, group1) &&
    codingComplete &&
    translationComplete &&
    configurationComplete;

  const canShowGlobalBreakFix =
    hasUserGroup(userContext, group1) &&
    selectedHealthPlan.healthPlanName === "AHCS" &&
    codingComplete &&
    translationComplete &&
    configurationComplete &&
    breakFix;

  const canShowPostCABUpdate =
    hasUserGroup(userContext, group2) &&
    selectedHealthPlan.healthPlanName === "AHCS" &&
    codingComplete &&
    translationComplete &&
    CAB;

  const canShowOffCycleVariance =
    hasUserGroup(userContext, group2) &&
    codingComplete &&
    translationComplete &&
    configurationComplete &&
    selectedHealthPlan.healthPlanName !== "AHCS";

  const openDialogFor = useCallback((type) => {
    // close the menu first (or after—batched either way)
    setAnchorEl(null);
    setselectedType(type);
  }, []);

  return (
    <Box sx={{ minWidth: 120 }}>
      <Button
        disabled={
          hasRestrictedChildPolicy ||
          (!canShowBreakFix &&
            !canShowGlobalBreakFix &&
            !canShowPostCABUpdate &&
            !canShowOffCycleVariance)
        }
        onClick={handleClick}
        endIcon={
          open ? (
            <ArrowDropUpIcon sx={{ ml: -1 }} />
          ) : (
            <ArrowDropDownIcon sx={{ ml: -1 }} />
          )
        }
      >
        Actions
      </Button>
      <Menu
        id="bulk-Updates-Select"
        aria-labelledby="bulk-Updates-Button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {canShowBreakFix && (
          <MenuItem onClick={() => openDialogFor(typeConstant.BREAKFIX)}>
            Break Fix
          </MenuItem>
        )}
        {canShowGlobalBreakFix && (
          <MenuItem onClick={() => openDialogFor(typeConstant.GLOBAL_BREAKFIX)}>
            Global Break Fix
          </MenuItem>
        )}
        {canShowPostCABUpdate && <MenuItem>Post-CAB Update</MenuItem>}
        {canShowOffCycleVariance && (
          <MenuItem
            onClick={() => openDialogFor(typeConstant.OFF_CYCLE_VARIANCE)}
          >
            Off-Cycle Variance
          </MenuItem>
        )}
      </Menu>
      {(selectedType === typeConstant.OFF_CYCLE_VARIANCE ||
        selectedType === typeConstant.BREAKFIX) && (
        <OffCycleVarianceDialog
          hpKey={healthPlanKey}
          open
          onClose={closeDialog}
          policyKey={policyKey}
          policyNumber={policyNumber}
          policyTitle={policyTitle}
          actionType={actionTypes[selectedType]}
          title={title[selectedType]}
          columnsName={columnsName[selectedType]}
          reloadGrid={reloadGrid}
        />
      )}
      {selectedType === typeConstant.GLOBAL_BREAKFIX && (
        <GlobalBreakFixDialog
          open
          onClose={(didApply) => {
            setselectedType(null);
            if (didApply) reloadGrid();
          }}
          policyKey={policyKey}
        />
      )}
    </Box>
  );
}
